<template>
  <div class="wrapper flex justify-center pb-14">
    <div class="flex">
      <div class="relative mr-[60px]">
        <UikitUserAvatar
          :user="account"
          size="xl"
          increasing
        />
        <div
          v-if="account.isTrusted"
          class="account__info__trust-mark"
        >
          <UIIcon name="trust-user-mark" />
        </div>
      </div>

      <div class="flex flex-col pt-3">
        <div class="flex items-end">
          <div class="text-[44px] leading-none playfair">
            {{ account.nickname }}
          </div>

          <LazyAccountAvatarLoader />
        </div>

        <div
          v-if="account.communityBadge"
          class="flex mt-2"
        >
          <account-o-community-badge
            :model-value="account.communityBadge"
            :user="account"
            with-dialog
            owner-mode
          />
        </div>

        <div class="flex mt-7 text-base text-[#bdbdbd]">
          <p
            v-if="account.registrationTime"
            class="account__info-description flex items-center"
          >
            {{ $t('account.AccountInfo.fromDate', { date: registrationDateStr }) }}
          </p>

          <div class="flex items-center">
            <template v-if="account.isPro">
              <div class="mr-2">
                {{ $t('account.AccountInfo.boutique') }}
              </div>
              <UIHint
                right
                content-width="300px"
              >
                {{ $t('account.AccountInfo.boutiqueHint') }}
              </UIHint>
            </template>

            <template v-else>
              <div class="mr-4">
                {{ $t('account.AccountInfo.privateSeller') }}
              </div>
              <UIHint>
                {{ $t('account.AccountInfo.privateSellerHint') }}
              </UIHint>
            </template>
          </div>
        </div>

        <div class="flex mt-8">
          <div class="mr-8">
            <div class="account__info__count-number">
              {{ account.productsCount }}
            </div>
            <div class="account__info__count-label">
              {{ $t('account.AccountInfo.productsCount', account.productsCount ?? 0) }}
            </div>
          </div>
          <div class="mr-8">
            <div class="account__info__count-number">
              {{ account.followersCount }}
            </div>
            <div class="account__info__count-label">
              {{ $t('account.AccountInfo.followersCount', account.followersCount ?? 0) }}
            </div>
          </div>
          <div>
            <div class="account__info__count-number">
              {{ account.followingsCount }}
            </div>
            <div class="account__info__count-label">
              {{ $t('account.AccountInfo.followingsCount', account.followingsCount ?? 0) }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import UIHint from '~/components/ui/UIHint.vue';
import UIIcon from '~/components/ui/UIIcon.vue';
import LazyAccountAvatarLoader from './AccountAvatarLoader.vue';

const { account, isAuth } = useAccountStore();
const { loadProductsCounts } = useProductCounts();

const { getFromMonthYear } = useDateFormatter();

const registrationDateStr = computed<string>(() => {
  if (!account.value.registrationTime) return '';

  return getFromMonthYear(account.value.registrationTime * 1000);
});

watch(() => account.value.id && isAuth.value, (isReady) => {
  if (!isReady) return;

  loadProductsCounts();
}, { immediate: true });
</script>

<style scoped lang="scss">
@tailwind components;

@layer components {
  .account__info {
    &-description {
      &::after {
        content: '';
        @apply inline-block w-[5px] h-[5px] bg-[#333] opacity-20 ml-2.5 mr-2.5;
      }
    }

    &__trust-mark {
      @apply flex items-center justify-center absolute top-1.5 right-0 w-9 h-9 rounded-full bg-white;
    }

    &__count {
      &-number {
        @apply font-semibold text-[19px] leading-none;
      }

      &-label {
        @apply text-sm text-[#828282] mt-0.5;
      }
    }
  }
}
</style>
